import { useLayoutEffect, useRef, useState } from "react";
import { roomLabels, resolveRoomDifficulty } from "../../Config/config";
import { RoomStates } from "../../Types/Game";
import { useDifficultyRatings } from "../../Context/DifficultyRatingsContext";
import DifficultyIndicator, {
  DIFFICULTY_NAMES,
} from "../../Components/DifficultyIndicator/DifficultyIndicator";
import "./RoomListPanels.css";

/**
 * RoomListPanels
 * --------------
 * Two vertically stacked panels for choosing which rooms are in an event:
 * - "Selected Rooms": ordered; drag within the panel to reorder.
 * - "Unselected Rooms": drag a room up into Selected (at any position) to
 *   include it; drag a selected room down here to remove it.
 *
 * The component is fully controlled: it derives both lists from `rooms`
 * (active + order) and reports every change through `onChange` with orders
 * renumbered 1..N for selected rooms, unselected rooms following after.
 */

// TYPES -------------------------------------------------------------
type Props = {
  rooms: RoomStates;
  readOnly?: boolean;
  onChange: (next: RoomStates) => void;
};

const EXPERIMENT_ROOM_ID = "room99";

/** Drop-zone sentinel for "end of the selected list". */
const END_OF_LIST = "__end__";

// COMPONENT ---------------------------------------------------------
const RoomListPanels = ({ rooms, readOnly = false, onChange }: Props) => {
  const { ratings: difficultyRatings } = useDifficultyRatings();

  /** STATE --------------------------------------------------------- */
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [dropTarget, setDropTarget] = useState<string | null>(null);

  /** FLIP ANIMATION -------------------------------------------------
   * Rows register their DOM nodes; commit() snapshots their positions
   * (First), and after React re-renders in the new order (Last) the
   * layout effect plays each row's positional delta (Invert + Play) so
   * users can see how the order shifted.
   */
  const rowRefs = useRef(new Map<string, HTMLDivElement>());
  const prevPositions = useRef<Map<string, DOMRect> | null>(null);

  const snapshotPositions = () => {
    const snap = new Map<string, DOMRect>();
    rowRefs.current.forEach((el, id) => snap.set(id, el.getBoundingClientRect()));
    prevPositions.current = snap;
  };

  useLayoutEffect(() => {
    const prev = prevPositions.current;
    if (!prev) return;
    prevPositions.current = null;

    rowRefs.current.forEach((el, id) => {
      const before = prev.get(id);
      if (!before) return;
      const after = el.getBoundingClientRect();
      const dx = before.left - after.left;
      const dy = before.top - after.top;
      if (dx === 0 && dy === 0) return;
      el.animate(
        [{ transform: `translate(${dx}px, ${dy}px)` }, { transform: "translate(0, 0)" }],
        { duration: 180, easing: "ease-out" }
      );
    });
  });

  /** DERIVED ------------------------------------------------------- */
  const byOrder = (a: string, b: string) => rooms[a].order - rooms[b].order;
  const selectedIds = Object.keys(rooms).filter((id) => rooms[id].active).sort(byOrder);
  const unselectedIds = Object.keys(rooms).filter((id) => !rooms[id].active).sort(byOrder);
  const isExperimentRoomSelected = Boolean(rooms[EXPERIMENT_ROOM_ID]?.active);

  /** HANDLERS ------------------------------------------------------ */
  /** Rebuild RoomStates from an ordered list of selected ids. */
  const commit = (nextSelected: string[]) => {
    snapshotPositions();

    const nextUnselected = Object.keys(rooms)
      .filter((id) => !nextSelected.includes(id))
      .sort(byOrder);

    const next: RoomStates = {};
    nextSelected.forEach((id, i) => {
      next[id] = { ...rooms[id], active: true, order: i + 1 };
    });
    nextUnselected.forEach((id, i) => {
      next[id] = { ...rooms[id], active: false, order: nextSelected.length + i + 1 };
    });
    onChange(next);
  };

  const clearDrag = () => {
    setDraggingId(null);
    setDropTarget(null);
  };

  /** The dragged room id travels in dataTransfer (state is styling-only). */
  const draggedIdFrom = (e: React.DragEvent): string | null => {
    const id = e.dataTransfer.getData("text/plain");
    return id && id in rooms ? id : null;
  };

  /**
   * Drop into Selected: the dragged room takes the target's current position
   * (or the end when `targetId` is null).
   * - Reorder downward (1 → 3): rooms in between shift up, dragged lands at 3.
   * - Reorder upward (8 → 5): rooms from 5 shift down, dragged lands at 5.
   * - From Unselected: rooms from the target's position always shift down.
   * All three cases fall out of using the target's index in the ORIGINAL
   * selected list as the insertion point after removing the dragged id.
   */
  const dropOnSelected = (e: React.DragEvent, targetId: string | null) => {
    const dragged = draggedIdFrom(e);
    if (!dragged || dragged === targetId) return clearDrag();

    const targetIndex = targetId ? selectedIds.indexOf(targetId) : -1;
    const without = selectedIds.filter((id) => id !== dragged);
    const insertAt = targetIndex >= 0 ? targetIndex : without.length;
    without.splice(insertAt, 0, dragged);

    commit(without);
    clearDrag();
  };

  /** Drop into Unselected: remove from the selection. */
  const dropOnUnselected = (e: React.DragEvent) => {
    const dragged = draggedIdFrom(e);
    if (!dragged) return clearDrag();
    commit(selectedIds.filter((id) => id !== dragged));
    clearDrag();
  };

  /** Click alternatives to drag-and-drop. */
  const removeRoom = (id: string) => commit(selectedIds.filter((s) => s !== id));
  const addRoom = (id: string) => commit([...selectedIds, id]);

  /** Swap a selected room with its neighbour (delta ±1). */
  const moveRoom = (id: string, delta: -1 | 1) => {
    const from = selectedIds.indexOf(id);
    const to = from + delta;
    if (from < 0 || to < 0 || to >= selectedIds.length) return;
    const next = [...selectedIds];
    [next[from], next[to]] = [next[to], next[from]];
    commit(next);
  };

  const dragProps = (id: string) =>
    readOnly
      ? {}
      : {
          draggable: true,
          onDragStart: (e: React.DragEvent) => {
            e.dataTransfer.effectAllowed = "move";
            e.dataTransfer.setData("text/plain", id);
            setDraggingId(id);
          },
          onDragEnd: clearDrag,
        };

  const allowDrop = (target: string) => (e: React.DragEvent) => {
    if (readOnly) return;
    e.preventDefault();
    // Item-level dragover must not bubble to the panel's handler, otherwise
    // dropTarget rapidly alternates between item and panel (orange flicker).
    e.stopPropagation();
    e.dataTransfer.dropEffect = "move";
    if (dropTarget !== target) setDropTarget(target);
  };

  /** RENDER -------------------------------------------------------- */
  const renderItem = (id: string, panel: "selected" | "unselected", index?: number) => (
    <div
      key={id}
      role="listitem"
      className="room-panel__row"
      ref={(el) => {
        if (el) rowRefs.current.set(id, el);
        else rowRefs.current.delete(id);
      }}
    >
      {panel === "selected" && (
        <span className="room-panel__row-order">{(index ?? 0) + 1}.</span>
      )}
      <div
        className={[
          "room-panel__item",
          id === EXPERIMENT_ROOM_ID ? "room-panel__item--experiment" : "",
          draggingId === id ? "room-panel__item--dragging" : "",
          dropTarget === id ? "room-panel__item--drop-target" : "",
          readOnly ? "room-panel__item--read-only" : "",
        ].join(" ").trim()}
        {...dragProps(id)}
        onDragOver={panel === "selected" ? allowDrop(id) : undefined}
        onDrop={
          panel === "selected"
            ? (e) => {
                e.preventDefault();
                e.stopPropagation();
                dropOnSelected(e, id);
              }
            : undefined
        }
      >
        <span className="room-panel__item-label">{roomLabels[id] ?? id}</span>
        <span
          className="room-panel__difficulty difficulty-tooltip"
          data-tooltip={`Difficulty: ${DIFFICULTY_NAMES[resolveRoomDifficulty(id, difficultyRatings)]}`}
          tabIndex={0}
        >
          <DifficultyIndicator
            level={resolveRoomDifficulty(id, difficultyRatings)}
            className="room-panel__difficulty-icon"
          />
        </span>
        {!readOnly && panel === "selected" && (
          <span className="room-panel__item-movers">
            <button
              type="button"
              className="room-panel__item-move"
              onClick={() => moveRoom(id, -1)}
              disabled={index === 0}
              aria-label={`Move ${roomLabels[id] ?? id} up`}
            >
              ▲
            </button>
            <button
              type="button"
              className="room-panel__item-move"
              onClick={() => moveRoom(id, 1)}
              disabled={index === selectedIds.length - 1}
              aria-label={`Move ${roomLabels[id] ?? id} down`}
            >
              ▼
            </button>
          </span>
        )}
        {!readOnly && (
          <button
            type="button"
            className={`room-panel__item-action ${
              panel === "selected"
                ? "room-panel__item-action--remove"
                : "room-panel__item-action--add"
            }`}
            onClick={() =>
              panel === "selected" ? removeRoom(id) : addRoom(id)
            }
            aria-label={
              panel === "selected"
                ? `Remove ${roomLabels[id] ?? id} from selected rooms`
                : `Add ${roomLabels[id] ?? id} to selected rooms`
            }
          >
            {panel === "selected" ? "×" : "+"}
          </button>
        )}
      </div>
    </div>
  );

  return (
    <div className="room-panels">
      <div
        className={`room-panel room-panel--selected ${
          dropTarget === END_OF_LIST ? "room-panel--drop-target" : ""
        }`}
        onDragOver={allowDrop(END_OF_LIST)}
        onDrop={(e) => {
          e.preventDefault();
          dropOnSelected(e, null);
        }}
      >
        <h3 className="room-panel__title">Selected Rooms</h3>
        <div className="room-panel__list" role="list" aria-label="Selected rooms, in play order">
          {selectedIds.length === 0 && (
            <p className="room-panel__empty">Drag rooms here to include them in the event.</p>
          )}
          {selectedIds.map((id, index) => renderItem(id, "selected", index))}
          {/* Faded placeholder for the next room while rooms remain unselected */}
          {unselectedIds.length > 0 && (
            <div className="room-panel__row" aria-hidden="true">
              <span className="room-panel__row-order room-panel__row-order--ghost">
                {selectedIds.length + 1}.
              </span>
              <div className="room-panel__item room-panel__item--ghost" />
            </div>
          )}
        </div>
      </div>

      <div
        className={`room-panel room-panel--unselected ${
          dropTarget === "unselected" ? "room-panel--drop-target" : ""
        }`}
        onDragOver={allowDrop("unselected")}
        onDrop={(e) => {
          e.preventDefault();
          dropOnUnselected(e);
        }}
      >
        <h3 className="room-panel__title">Unselected Rooms</h3>
        <div className="room-panel__list" role="list" aria-label="Unselected rooms">
          {unselectedIds.length === 0 && (
            <p className="room-panel__empty">All rooms are selected.</p>
          )}
          {unselectedIds.map((id) => renderItem(id, "unselected"))}
        </div>
      </div>

      {isExperimentRoomSelected && (
        <p className="room-panels__warning" role="alert">
          Experiment Room is designed for testing new activities.
        </p>
      )}
    </div>
  );
};

export default RoomListPanels;
